<template>
    <div class="mt-4">
        <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm mb-2">Membros do calendário:</h3>
            <button @click="openAddModal" class="text-xs p-1 border rounded-full px-3 hover:bg-blue-100">
                + Adicionar Membro
            </button>
        </div>

        <div class="border px-4 py-1 rounded-lg bg-gray-50">
            <div v-for="(member, index) in members" :key="member.id"
                class="flex items-center justify-between gap-4 border-b py-2 last:border-b-0">
                <div class="flex items-center gap-4">
                    <div class="h-[18px] w-[18px] rounded-full" :style="{ backgroundColor: member.cor }"></div>
                    <div>
                        <p class="font-medium text-sm">{{ member.name }}</p>
                        <p class="text-xs text-gray-500">{{ member.function }}</p>
                    </div>
                </div>
                <div class="flex gap-1">
                    <button @click="editMember(index)"
                        class="text-blue-500 text-xs p-1 border rounded-full px-3 hover:bg-blue-100">Editar</button>
                    <button @click="confirmRemoveMember(index)"
                        class="text-red-500 text-xs p-1 border rounded-full px-3 hover:bg-rose-100">Remover</button>
                </div>
            </div>
        </div>

        <!-- Modal para Adicionar/Editar Membro -->
        <div v-if="isModalOpen" class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-lg">
                <div class="bg-gray-100 px-4 py-3 rounded-t-lg border-b">
                    <h3 class="text-lg font-medium">
                        {{ isEditing ? 'Editar Membro' : 'Adicionar Membro' }}
                    </h3>
                </div>

                <div class="p-6 flex flex-col gap-4">
                    <div>
                        <label class="text-xs mb-1 block">Nome do membro:</label>
                        <input type="text" v-model="modalMemberData.name"
                            class="w-full border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            :class="{ 'border-red-500': errors.name }">
                        <p v-if="errors.name" class="text-xs text-red-500 mt-1">O nome do membro é obrigatório.</p>
                    </div>
                    <div>
                        <label class="text-xs mb-1 block">Função do membro:</label>
                        <input type="text" v-model="modalMemberData.function"
                            class="w-full border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            :class="{ 'border-red-500': errors.function }">
                        <p v-if="errors.function" class="text-xs text-red-500 mt-1">A função do membro é obrigatória.
                        </p>
                    </div>
                    <div>
                        <label class="text-xs mb-1 block">Cor:</label>
                        <input type="color" v-model="modalMemberData.cor"
                            class="h-[40px] w-[40px] rounded-md cursor-pointer border"
                            :class="{ 'border-red-500': errors.cor }">
                        <p v-if="errors.cor" class="text-xs text-red-500 mt-1">A cor do membro é obrigatória.</p>
                    </div>
                </div>

                <div class="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end gap-2 border-t">
                    <button @click="closeModal"
                        class="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button @click="saveMember"
                        class="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600">
                        {{ isEditing ? 'Salvar' : 'Adicionar' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal de Confirmação para Remover Membro -->
        <div v-if="isConfirmRemoveOpen"
            class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md">
                <div class="bg-gray-100 px-4 py-3 rounded-t-lg border-b">
                    <h3 class="text-lg font-medium">Remover Membro</h3>
                </div>
                <div class="p-6">
                    <p class="text-sm text-gray-700 mb-4">
                        Ao remover este membro, todos os eventos associados a ele serão permanentemente excluídos. Esta
                        ação é irreversível. Deseja realmente continuar?
                    </p>
                    <div class="flex justify-end gap-2">
                        <button @click="cancelRemove"
                            class="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                        <button @click="removeMember(confirmRemoveIndex)"
                            class="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600">Remover</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
export default {
    props: {
        initialMembers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isConfirmRemoveOpen: false,

            members: [],
            isModalOpen: false,
            isEditing: false,
            editMemberIndex: null,
            modalMemberData: {
                id: "",
                name: "",
                cor: "#0441db",
                function: ""
            },
            errors: {
                name: false,
                cor: false,
                function: false
            }
        };
    },
    created() {
        this.members = [...this.initialMembers];
    },
    methods: {
        /** Abrir modal para adicionar um novo membro */
        openAddModal() {
            this.isEditing = false;
            this.modalMemberData = {
                id: "",
                name: "",
                cor: this.generateRandomColor(),
                function: ""
            };
            this.errors = { name: false, cor: false, function: false };
            this.isModalOpen = true;
        },

        /** Editar membro existente */
        editMember(index) {
            this.isEditing = true;
            this.editMemberIndex = index;
            this.modalMemberData = { ...this.members[index] };
            this.errors = { name: false, cor: false, function: false };
            this.isModalOpen = true;
        },

        generateRandomColor() {
            const randomChannel = () => Math.floor(Math.random() * (200 - 50) + 50); // Garante valores entre 50 e 200
            const r = randomChannel().toString(16).padStart(2, '0');
            const g = randomChannel().toString(16).padStart(2, '0');
            const b = randomChannel().toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        },


        /** Validar campos do modal */
        validateFields() {
            this.errors.name = !this.modalMemberData.name.trim();
            this.errors.function = !this.modalMemberData.function.trim();
            this.errors.cor = !this.modalMemberData.cor;

            return !this.errors.name && !this.errors.function && !this.errors.cor;
        },

        /** Salvar novo membro ou editar existente */
        saveMember() {
            if (!this.validateFields()) {
                return;
            }

            if (this.isEditing && this.editMemberIndex !== null) {
                this.members[this.editMemberIndex] = { ...this.modalMemberData };
            } else {
                this.modalMemberData.id = `${Math.random().toString(36).substr(2, 9)}`;
                this.members.push({ ...this.modalMemberData });
            }

            this.$emit('update-members', this.members);
            this.closeModal();
        },

        confirmRemoveMember(index) {
            this.confirmRemoveIndex = index;
            this.isConfirmRemoveOpen = true;
        },

        /** Cancelar remoção */
        cancelRemove() {
            this.confirmRemoveIndex = null;
            this.isConfirmRemoveOpen = false;
        },

        /** Remover membro */
        removeMember(index) {
            this.members.splice(index, 1);
            this.$emit('update-members', this.members);
            this.isConfirmRemoveOpen = false;
            this.confirmRemoveIndex = null;
        },

        /** Fechar modal */
        closeModal() {
            this.isModalOpen = false;
            this.isEditing = false;
            this.editMemberIndex = null;
            this.modalMemberData = {
                id: "",
                name: "",
                cor: "#0441db",
                function: ""
            };
            this.errors = { name: false, cor: false, function: false };
        }
    }
};

</script>

<style>
.modal {
    z-index: 1000;
}
</style>
